import type { MouseEventHandler } from 'react';

import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserContext, type UserContextValue } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useIsDryRunMode from '@core/hooks/useIsDryRunMode';
import useIsMigrationPreview from '@core/hooks/useIsMigrationPreview';
import { useProjectStore, useSuperHubStore } from '@core/store';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Title from '@ui/Title';

import TrialExpiredModal from '../TrialExpiredModal';

import styles from './index.module.scss';

const urlRegex = /^https?:\/\//i;

type SectionType = 'changelog' | 'customPage' | 'discussion' | 'guide' | 'history' | 'recipe' | 'reference' | 'review';

export interface EmptyStateProps {
  /**
   * When provided, renders a CTA below the title and description as a button
   * link that navigates users to the absolute URL or relative path provided.
   */
  action?: string;
  className?: string;
  /**
   * When provided, renders a CTA whose clicks are handled by this callback.
   */
  onAction?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  /** Required field determines the content and copy to display to users. */
  section: SectionType;
}

/**
 * Renders an empty state for our hub sections. An optional CTA is rendered when
 * the `action` prop is provided with a truthy value. Use it to render a
 * clickable link or button that can then be handled with the `onAction` prop.
 * @example
 * ```tsx
 * <EmptyState section='changelog' action='/create/changelog' />
 * ```
 */
export default function EmptyState({ action, className, onAction, section }: EmptyStateProps) {
  const bem = useClassy(styles, 'EmptyState');
  const { isMigrationPreview } = useIsMigrationPreview();
  const user = useContext(UserContext) as unknown as UserContextValue;
  const { isGod } = user;
  const { isDryRunMode } = useIsDryRunMode();
  const { t } = useTranslation();
  const [isSuperHubAdmin, isAdminUIHidden] = useSuperHubStore(s => [s.user.role === 'admin', s.isAdminUIHidden]);
  const firstPage = useProjectStore(s => s.data.appearance.navigation.first_page);
  const isFirstPageSection =
    (firstPage === 'documentation' && section === 'guide') || (firstPage === 'reference' && section === 'reference');
  const useViewerFacingTemplate =
    !isSuperHubAdmin || section === 'discussion' || (isAdminUIHidden && isFirstPageSection);

  /** Hash of empty state content based on section. */
  const template = useMemo(
    () =>
      ({
        changelog: {
          actionLabel: 'Create Post',
          description: 'The Changelog keeps your end-users up to date on your latest updates and announcements.',
          docsLink: 'https://docs.readme.com/main/docs/changelog',
          icon: 'megaphone',
          title: useViewerFacingTemplate ? t('emptyState.changelog.title') : 'Changelog',
        },
        customPage: {
          actionLabel: 'Create Custom Page',
          description:
            'Custom pages enable you to create content outside of the primary docs sections. These can be added to your main navigation and linked to from anywhere in your project.',
          docsLink: 'https://docs.readme.com/main/docs/custom-page',
          icon: 'custom-pages',
          title: 'Custom Pages',
        },
        discussion: {
          actionLabel: t('emptyState.discussion.actionLabel'),
          description: t('emptyState.discussion.description'),
          docsLink: undefined,
          icon: 'message-circle',
          title: t('emptyState.discussion.title'),
        },
        guide: {
          actionLabel: 'Create Guides Page',
          description:
            'Guides transform complex concepts into clear, engaging documentation that helps developers succeed with your API.',
          docsLink: undefined,
          icon: 'book-open',
          title: useViewerFacingTemplate ? t('emptyState.guide.title') : 'Guides',
        },
        history: {
          actionLabel: '',
          description: '',
          docsLink: undefined,
          icon: 'clock',
          title: 'No History',
        },
        recipe: {
          actionLabel: 'Create Recipe',
          description:
            'Recipes are task-driven guidance for your API that transform intimidating code blocks into step-by-step walkthroughs.',
          docsLink: undefined,
          icon: 'recipes',
          title: useViewerFacingTemplate ? t('emptyState.recipe.title') : 'Recipes',
        },
        reference: {
          actionLabel: 'Add API Definition',
          description:
            'The definitive technical guide to your API, documenting every endpoint, parameter, and response code in detail. Upload an OpenAPI file or use our API designer to get started.',
          docsLink: 'https://docs.readme.com/main/docs/api-reference',
          icon: 'code',
          title: useViewerFacingTemplate ? t('emptyState.reference.title') : 'API Reference',
        },
        review: {
          actionLabel: '',
          description: '',
          docsLink: undefined,
          icon: 'badge-check',
          title: 'No Changes to Review',
        },
      }) satisfies Record<
        SectionType,
        { title: string; description: string; icon: string; actionLabel: string; docsLink?: string }
      >,
    [t, useViewerFacingTemplate],
  );
  const { title, description, icon, actionLabel, docsLink } = template[section];

  /** Only allow actionable state if not in preview mode or dry run mode, unless user is god. */
  const isActionableInPreview = (!isMigrationPreview && !isDryRunMode) || isGod;
  /** Whether this empty state should be actionable or not. */
  const isActionable = (!!action || !!onAction) && !!actionLabel && isActionableInPreview;
  const isAbsoluteUrl = !!action && urlRegex.test(action);
  const isRelativePath = !!action && !isAbsoluteUrl;

  const isTrialExpired = useProjectStore(s => s.plan.isTrialExpired);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // Viewer-facing empty state
  if (useViewerFacingTemplate) {
    return (
      <Flex align="center" className={bem('&', className)} gap={0} justify="center">
        <Flex align="center" className={bem('-content')} gap="size-24" layout="col">
          <Icon color="blue" name={icon} size={40} />
          <Flex align="center" gap={0} layout="col">
            <Title as={2} className={bem('-title')} level={3}>
              {title}
            </Title>
            {section === 'discussion' && (
              <Flex align="center" gap="size-24" layout="col">
                <p className={bem('-description')}>{description}</p>
                <Button
                  {...(isAbsoluteUrl && { href: action })}
                  kind="primary"
                  onClick={onAction}
                  size="sm"
                  {...(isRelativePath && { to: action })}
                >
                  {actionLabel}
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex align="center" className={bem('&', className)} gap={0} justify="center">
      <Flex align="center" className={bem('-container')} gap="size-12" layout="col">
        <Flex align="start" className={bem('-content', '-content_admin')} gap="size-24" layout="col">
          <Icon color="blue" name={icon} size={40} />

          <Flex align="start" gap="size-8" layout="col">
            <Title as={2} className={bem('-title')} level={3}>
              {title}
            </Title>
            {!!isActionable && <p className={bem('-description')}>{description}</p>}
          </Flex>

          {!!isActionable && (
            <Flex className={bem('-actions')} gap="size-8">
              {/* Handle the trial expired state of recipes and discussions, since they have different edit modes */}
              {!!isTrialExpired && section === 'recipe' ? (
                <Button kind="primary" onClick={() => setShowTrialExpiredModal(true)} size="sm">
                  {actionLabel}
                </Button>
              ) : (
                <Button
                  {...(isAbsoluteUrl && { href: action })}
                  kind="primary"
                  onClick={onAction}
                  size="sm"
                  {...(isRelativePath && { to: action })}
                >
                  {actionLabel}
                </Button>
              )}

              {section === 'recipe' && (
                <Button
                  href="https://docs.readme.com/recipes/publish-all-docs-in-category"
                  kind="secondary"
                  outline
                  size="sm"
                  target="_blank"
                >
                  Try Example
                  <Icon name="arrow-up-right" />
                </Button>
              )}

              {!!docsLink && (
                <Button href={docsLink} kind="secondary" outline size="sm" target="_blank">
                  Docs
                  <Icon aria-label="Opens in a new tab" name="arrow-up-right" />
                </Button>
              )}
            </Flex>
          )}
        </Flex>

        <Flex align="center" className={bem('-tip')} gap="size-4">
          <Icon className={bem('-tip-icon')} name="eye-off" size="sm" />
          <span>Empty sections are hidden to viewers</span>
        </Flex>
      </Flex>
      {!!showTrialExpiredModal && (
        <TrialExpiredModal
          handleClose={() => setShowTrialExpiredModal(false)}
          modalTargetId="trial-expired-modal-target"
        />
      )}
    </Flex>
  );
}
